import React, { useEffect, Suspense, lazy } from "react";
import "./App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

// Eagerly loaded — needed on first paint of every page or are tiny wrappers.
import Layout from "./components/Layout";
import ErrorBoundary from "./components/ErrorBoundary";
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminLogin from "./pages/admin/AdminLogin";
import DtmLayout from "./pages/dtm/DtmLayout";
import DtmLogin from "./pages/dtm/DtmLogin";
import DtmAcceptInvite from "./pages/dtm/DtmAcceptInvite";
import AffiliateLayout from "./pages/affiliate/AffiliateLayout";
import AffiliateLogin from "./pages/affiliate/AffiliateLogin";
import MyAccountGate from "./components/MyAccountGate";
import ChatWidgetGate from "./components/ChatWidgetGate";
import GAPageViewTracker from "./components/GAPageViewTracker";
import ReferralClickTracker from "./components/ReferralClickTracker";
import FreeSignupPopup from "./components/FreeSignupPopup";
import CheckoutGate from "./components/CheckoutGate";
import CookieConsent from "./components/CookieConsent";
import NewsletterPopup from "./components/NewsletterPopup";

import { TrackingProvider } from "./contexts/TrackingContext";
import { SiteContentProvider } from "./contexts/SiteContentContext";
import { AdminAuthProvider } from "./contexts/AdminAuthContext";
import { DtmAuthProvider } from "./contexts/DtmAuthContext";
import { AffiliateAuthProvider } from "./contexts/AffiliateAuthContext";
import { UserAuthProvider } from "./contexts/UserAuthContext";

import { LEGACY_REDIRECTS } from "./legacyRedirects";

// ---- Lazy-loaded route components (one webpack chunk each) ----
const BecomeDriver = lazy(() => import("./pages/BecomeDriver"));
const CVReview = lazy(() => import("./pages/CVReview"));
const Interview = lazy(() => import("./pages/Interview"));
const InterviewCoaching = lazy(() => import("./pages/InterviewCoaching"));
const InterviewCoachingSuccess = lazy(() => import("./pages/InterviewCoachingSuccess"));
const InterviewCoachingTerms = lazy(() => import("./pages/InterviewCoachingTerms"));
const Psychometric = lazy(() => import("./pages/Psychometric"));
const OnlineAssessment = lazy(() => import("./pages/OnlineAssessment"));
const CPDCourses = lazy(() => import("./pages/CPDCourses"));
const Careers = lazy(() => import("./pages/Careers"));
const CompletePackage = lazy(() => import("./pages/CompletePackage"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Contact = lazy(() => import("./pages/Contact"));
const ServiceLeavers = lazy(() => import("./pages/ServiceLeavers"));
const WhatDoesATrainDriverDo = lazy(() => import("./pages/WhatDoesATrainDriverDo"));
const MyTrainDriverJourney = lazy(() => import("./pages/MyTrainDriverJourney"));
const Preview = lazy(() => import("./pages/preview"));
const SubmitCV = lazy(() => import("./pages/SubmitCV"));
const DownloadInterview = lazy(() => import("./pages/DownloadInterview"));
const OnlineAssessmentTool = lazy(() => import("./pages/OnlineAssessmentTool"));
const FullPackageAccess = lazy(() => import("./pages/FullPackageAccess"));
const CompletePackageAccess = lazy(() => import("./pages/CompletePackageAccess"));
const EligibilityWizard = lazy(() => import("./pages/EligibilityWizard"));
const StartYourJourney = lazy(() => import("./pages/StartYourJourney"));
const Products = lazy(() => import("./pages/Products"));
const TocsDirectory = lazy(() => import("./pages/TocsDirectory"));
const TocDetail = lazy(() => import("./pages/TocDetail"));
const Outcomes = lazy(() => import("./pages/Outcomes"));
const CVCheckerApp = lazy(() => import("./pages/CVCheckerApp"));
const DtmDashboard = lazy(() => import("./pages/dtm/DtmDashboard"));
const DtmCall = lazy(() => import("./pages/dtm/DtmCall"));
const GuestCall = lazy(() => import("./pages/interview/GuestCall"));
const InterviewSimulatorApp = lazy(() => import("./pages/InterviewSimulatorApp"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const MyAccount = lazy(() => import("./pages/MyAccount"));
const AuthLogin = lazy(() => import("./pages/AuthLogin"));
const AuthVerify = lazy(() => import("./pages/AuthVerify"));
const ArmedForcesWeek = lazy(() => import("./pages/ArmedForcesWeek"));
const Blog = lazy(() => import("./pages/Blog"));
const FutureDriver = lazy(() => import("./pages/FutureDriver"));
const Verify = lazy(() => import("./pages/Verify"));
const InterviewProgress = lazy(() => import("./pages/InterviewProgress"));
const JourneyStart = lazy(() => import("./pages/JourneyStart"));
const ReferralProgramme = lazy(() => import("./pages/ReferralProgramme"));
const ReferralTerms = lazy(() => import("./pages/ReferralTerms"));
const ReferralRedirect = lazy(() => import("./pages/ReferralRedirect"));
const AffiliateAcceptInvite = lazy(() => import("./pages/affiliate/AffiliateAcceptInvite"));
const AffiliateDashboard = lazy(() => import("./pages/affiliate/AffiliateDashboard"));

const WhyMostApplicantsFail = lazy(() => import("./pages/blog/WhyMostApplicantsFail"));
const WhatTrainDriverRecruitersLookFor = lazy(() => import("./pages/blog/WhatTrainDriverRecruitersLookFor"));
const AssessmentDayReality = lazy(() => import("./pages/blog/AssessmentDayReality"));
const RailwayExperienceDoesNotGuaranteeSuccess = lazy(() => import("./pages/blog/RailwayExperienceDoesNotGuaranteeSuccess"));

const HowToBecomeATrainDriver = lazy(() => import("./pages/courses/HowToBecomeATrainDriver"));
const IntroToPersonalTrackSafety = lazy(() => import("./pages/courses/IntroToPersonalTrackSafety"));
const IntroToTractionAndRollingStock = lazy(() => import("./pages/courses/IntroToTractionAndRollingStock"));
const IntroToTrainDriving = lazy(() => import("./pages/courses/IntroToTrainDriving"));
const OperationalThinkingL3Landing = lazy(() => import("./pages/courses/OperationalThinkingL3Landing"));
const RiskBasedDecisionMakingL3Landing = lazy(() => import("./pages/courses/RiskBasedDecisionMakingL3Landing"));
const DevelopingOperationalMaturityLanding = lazy(() => import("./pages/courses/DevelopingOperationalMaturityLanding"));
const PetLevel3Landing = lazy(() => import("./pages/courses/PetLevel3Landing"));
const CoursePlayer = lazy(() => import("./pages/courses/CoursePlayer"));
const AssessmentRunner = lazy(() => import("./pages/courses/AssessmentRunner"));
const AssessmentFeedback = lazy(() => import("./pages/courses/AssessmentFeedback"));
const CertificateView = lazy(() => import("./pages/courses/CertificateView"));
const CourseFeedback = lazy(() => import("./pages/courses/CourseFeedback"));
const CapstoneScenario = lazy(() => import("./pages/courses/CapstoneScenario"));
const RiskProfile = lazy(() => import("./pages/courses/RiskProfile"));

const AdminLMS = lazy(() => import("./pages/admin/AdminLMS"));
const AdminLearnerDetail = lazy(() => import("./pages/admin/AdminLearnerDetail"));
const AdminCertificates = lazy(() => import("./pages/admin/AdminCertificates"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminInbox = lazy(() => import("./pages/admin/AdminInbox"));
const AdminSubscribers = lazy(() => import("./pages/admin/AdminSubscribers"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminCustomers = lazy(() => import("./pages/admin/AdminCustomers"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminAbandonedCarts = lazy(() => import("./pages/admin/AdminAbandonedCarts"));
const AdminLifecycle = lazy(() => import("./pages/admin/AdminLifecycle"));
const AdminPayments = lazy(() => import("./pages/admin/AdminPayments"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminContent = lazy(() => import("./pages/admin/AdminContent"));
const AdminMedia = lazy(() => import("./pages/admin/AdminMedia"));
const AdminJobs = lazy(() => import("./pages/admin/AdminJobs"));
const AdminChangePassword = lazy(() => import("./pages/admin/AdminChangePassword"));
const AdminOnlineAssessment = lazy(() => import("./pages/admin/AdminOnlineAssessment"));
const AdminReferrals = lazy(() => import("./pages/admin/AdminReferrals"));
const AdminInterviewCoaches = lazy(() => import("./pages/admin/AdminInterviewCoaches"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function RouteFallback() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]" aria-busy="true" data-testid="route-loading">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#E63B2E] rounded-full animate-spin" />
    </div>
  );
}

function PublicShell({ children }) {
  return (
    <Layout>
      <Suspense fallback={<RouteFallback />}>
        {children}
      </Suspense>
      <FreeSignupPopup />
    </Layout>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <ScrollToTop />
          <ChatWidgetGate />
          <CookieConsent />
          <NewsletterPopup />
          <GAPageViewTracker />
          <ReferralClickTracker />

          <SiteContentProvider>
            <ErrorBoundary>
            <TrackingProvider>
              <AdminAuthProvider>
                <DtmAuthProvider>
                <AffiliateAuthProvider>
                <UserAuthProvider>
                  <Routes>
                    {/* Admin routes */}
                    <Route path="/admin/login" element={<AdminLogin />} />

                    {/* DTM (Driver Team Manager) coaching portal */}
                    <Route path="/dtm/login" element={<DtmLogin />} />
                    <Route path="/dtm/accept-invite" element={<DtmAcceptInvite />} />
                    <Route path="/interview/join/:bookingId" element={<GuestCall />} />
                    <Route path="/dtm" element={<DtmLayout />}>
                      <Route index element={<DtmDashboard />} />
                      <Route path="call/:bookingId" element={<DtmCall />} />
                    </Route>

                    {/* Referral Partner (affiliate) portal — invite-only */}
                    <Route path="/affiliate/login" element={<AffiliateLogin />} />
                    <Route path="/affiliate/accept-invite" element={<AffiliateAcceptInvite />} />
                    <Route path="/affiliate" element={<AffiliateLayout />}>
                      <Route index element={<AffiliateDashboard />} />
                    </Route>

                    <Route path="/admin" element={<AdminLayout />}>
                      <Route index element={<AdminDashboard />} />
                      <Route path="content" element={<AdminContent />} />
                      <Route path="inbox" element={<AdminInbox />} />
                      <Route path="subscribers" element={<AdminSubscribers />} />
                      <Route path="users" element={<AdminUsers />} />
                      <Route path="customers" element={<AdminCustomers />} />
                      <Route path="products" element={<AdminProducts />} />
                      <Route path="abandoned-carts" element={<AdminAbandonedCarts />} />
                      <Route path="lifecycle" element={<AdminLifecycle />} />
                      <Route path="online-assessment" element={<AdminOnlineAssessment />} />
                      <Route path="payments" element={<AdminPayments />} />
                      <Route path="lms" element={<AdminLMS />} />
                      <Route
                        path="lms/:slug/learners/:email"
                        element={<AdminLearnerDetail />}
                      />
                      <Route path="certificates" element={<AdminCertificates />} />
                      <Route path="analytics" element={<AdminAnalytics />} />
                      <Route path="media" element={<AdminMedia />} />
                      <Route path="jobs" element={<AdminJobs />} />
                      <Route path="password" element={<AdminChangePassword />} />
                      <Route path="referrals" element={<AdminReferrals />} />
                      <Route path="interview-coaches" element={<AdminInterviewCoaches />} />
                    </Route>

                    {/* Public site */}
                    <Route path="/" element={<PublicShell><Home /></PublicShell>} />
                    <Route path="/become-a-driver" element={<PublicShell><BecomeDriver /></PublicShell>} />
                    <Route path="/become-a-train-driver-at-18" element={<PublicShell><FutureDriver /></PublicShell>} />
                    <Route path="/cv" element={<PublicShell><CVReview /></PublicShell>} />
                    <Route path="/interview" element={<PublicShell><Interview /></PublicShell>} />
                    <Route path="/interview-coaching" element={<PublicShell><InterviewCoaching /></PublicShell>} />
                    <Route path="/interview-coaching/success" element={<PublicShell><InterviewCoachingSuccess /></PublicShell>} />
                    <Route path="/interview-coaching/terms" element={<PublicShell><InterviewCoachingTerms /></PublicShell>} />
                    <Route path="/psychometrics" element={<PublicShell><Psychometric /></PublicShell>} />
                    <Route path="/online-assessment" element={<PublicShell><OnlineAssessment /></PublicShell>} />
                    <Route path="/cpd" element={<PublicShell><CPDCourses /></PublicShell>} />

                    <Route path="/cpd/how-to-become-a-train-driver" element={<PublicShell><HowToBecomeATrainDriver /></PublicShell>} />
                    <Route path="/cpd/introduction-to-personal-track-safety" element={<PublicShell><IntroToPersonalTrackSafety /></PublicShell>} />
                    <Route path="/cpd/introduction-to-traction-and-rolling-stock" element={<PublicShell><IntroToTractionAndRollingStock /></PublicShell>} />
                    <Route path="/cpd/introduction-to-train-driving" element={<PublicShell><IntroToTrainDriving /></PublicShell>} />
                    <Route path="/cpd/pre-employment-training" element={<Navigate to="/cpd/pet-level-3-cpd-40-hours" replace />} />

                    <Route path="/careers" element={<PublicShell><Careers /></PublicShell>} />
                    <Route path="/complete-career-package" element={<PublicShell><CompletePackage /></PublicShell>} />
                    <Route path="/privacy-policy" element={<PublicShell><Privacy /></PublicShell>} />
                    <Route path="/contact" element={<PublicShell><Contact /></PublicShell>} />
                    <Route path="/service-leavers" element={<PublicShell><ServiceLeavers /></PublicShell>} />
                    <Route path="/what-does-a-train-driver-do" element={<PublicShell><WhatDoesATrainDriverDo /></PublicShell>} />
                    <Route path="/my-train-driver-journey" element={<PublicShell><MyTrainDriverJourney /></PublicShell>} />
                    <Route path="/preview" element={<PublicShell><Preview /></PublicShell>} />
                    <Route path="/eligibility" element={<Navigate to="/start-your-journey#eligibility" replace />} />
                    <Route path="/start-your-journey" element={<PublicShell><StartYourJourney /></PublicShell>} />
                    <Route path="/products" element={<PublicShell><Products /></PublicShell>} />
                    <Route path="/tocs" element={<PublicShell><TocsDirectory /></PublicShell>} />
                    <Route path="/tocs/:slug" element={<PublicShell><TocDetail /></PublicShell>} />
                    <Route path="/outcomes" element={<PublicShell><Outcomes /></PublicShell>} />
                    <Route path="/journey/start" element={<PublicShell><JourneyStart /></PublicShell>} />

                    <Route path="/cv-checker" element={<Navigate to="/cv" replace />} />
                    <Route path="/cv-checker/app" element={<PublicShell><CVCheckerApp /></PublicShell>} />
                    <Route path="/cv-checker/success" element={<PublicShell><PaymentSuccess /></PublicShell>} />
                    <Route path="/interview-simulator/app" element={<PublicShell><InterviewSimulatorApp /></PublicShell>} />
                    <Route path="/payments/success" element={<PublicShell><PaymentSuccess /></PublicShell>} />

                    <Route path="/auth/login" element={<PublicShell><AuthLogin /></PublicShell>} />
                    <Route path="/auth/verify" element={<PublicShell><AuthVerify /></PublicShell>} />
                    <Route path="/my-account" element={<PublicShell><MyAccount /></PublicShell>} />

                    <Route path="/my-account/cv-review" element={<PublicShell><MyAccountGate entitlement="cv_review" productLabel="CV Review (Expert)"><SubmitCV /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/interview-guide" element={<PublicShell><MyAccountGate entitlement="interview_guide" productLabel="Interview Preparation Guide"><DownloadInterview /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/online-assessment" element={<PublicShell><MyAccountGate entitlement="oa_tool" productLabel="Online Assessment Tool"><OnlineAssessmentTool /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/full-package" element={<PublicShell><MyAccountGate entitlement="full_package" productLabel="Full Career Package"><FullPackageAccess /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/complete-package" element={<PublicShell><MyAccountGate entitlement="complete_package" productLabel="Complete Career Package"><CompletePackageAccess /></MyAccountGate></PublicShell>} />

                    <Route path="/cpd/operational-thinking-l3" element={<PublicShell><OperationalThinkingL3Landing /></PublicShell>} />
                    <Route path="/my-account/courses/operational-thinking-l3" element={<PublicShell><MyAccountGate entitlement="cpd_operational_thinking_l3" productLabel="Operational Thinking — Intermediate"><CoursePlayer slug="operational-thinking-l3" /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/courses/operational-thinking-l3/assessment" element={<PublicShell><MyAccountGate entitlement="cpd_operational_thinking_l3" productLabel="Final Assessment"><AssessmentRunner slug="operational-thinking-l3" /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/courses/operational-thinking-l3/assessment-feedback" element={<PublicShell><MyAccountGate entitlement="cpd_operational_thinking_l3" productLabel="Your feedback"><AssessmentFeedback slug="operational-thinking-l3" /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/courses/operational-thinking-l3/certificate" element={<PublicShell><MyAccountGate entitlement="cpd_operational_thinking_l3" productLabel="Certificate"><CertificateView slug="operational-thinking-l3" /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/courses/operational-thinking-l3/feedback" element={<PublicShell><MyAccountGate entitlement="cpd_operational_thinking_l3" productLabel="Course feedback"><CourseFeedback slug="operational-thinking-l3" /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/courses/operational-thinking-l3/capstone" element={<PublicShell><MyAccountGate entitlement="cpd_operational_thinking_l3" productLabel="Capstone scenario"><CapstoneScenario slug="operational-thinking-l3" /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/courses/operational-thinking-l3/risk-profile" element={<PublicShell><MyAccountGate entitlement="cpd_operational_thinking_l3" productLabel="Risk profile"><RiskProfile slug="operational-thinking-l3" /></MyAccountGate></PublicShell>} />

                    <Route path="/cpd/risk-based-decision-making-l3" element={<PublicShell><RiskBasedDecisionMakingL3Landing /></PublicShell>} />
                    <Route path="/my-account/courses/risk-based-decision-making-l3" element={<PublicShell><MyAccountGate entitlement="cpd_risk_based_decision_making_l3" productLabel="Risk-Based Decision Making — Intermediate"><CoursePlayer slug="risk-based-decision-making-l3" /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/courses/risk-based-decision-making-l3/assessment" element={<PublicShell><MyAccountGate entitlement="cpd_risk_based_decision_making_l3" productLabel="Final Assessment"><AssessmentRunner slug="risk-based-decision-making-l3" /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/courses/risk-based-decision-making-l3/assessment-feedback" element={<PublicShell><MyAccountGate entitlement="cpd_risk_based_decision_making_l3" productLabel="Your feedback"><AssessmentFeedback slug="risk-based-decision-making-l3" /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/courses/risk-based-decision-making-l3/certificate" element={<PublicShell><MyAccountGate entitlement="cpd_risk_based_decision_making_l3" productLabel="Certificate"><CertificateView slug="risk-based-decision-making-l3" /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/courses/risk-based-decision-making-l3/feedback" element={<PublicShell><MyAccountGate entitlement="cpd_risk_based_decision_making_l3" productLabel="Course feedback"><CourseFeedback slug="risk-based-decision-making-l3" /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/courses/risk-based-decision-making-l3/capstone" element={<PublicShell><MyAccountGate entitlement="cpd_risk_based_decision_making_l3" productLabel="Capstone scenario"><CapstoneScenario slug="risk-based-decision-making-l3" /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/courses/risk-based-decision-making-l3/risk-profile" element={<PublicShell><MyAccountGate entitlement="cpd_risk_based_decision_making_l3" productLabel="Risk profile"><RiskProfile slug="risk-based-decision-making-l3" /></MyAccountGate></PublicShell>} />

                    <Route path="/cpd/developing-operational-maturity" element={<PublicShell><DevelopingOperationalMaturityLanding /></PublicShell>} />
                    <Route path="/my-account/courses/developing-operational-maturity" element={<PublicShell><MyAccountGate entitlement="cpd_developing_operational_maturity" productLabel="Developing Operational Maturity"><CoursePlayer slug="developing-operational-maturity" /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/courses/developing-operational-maturity/assessment" element={<PublicShell><MyAccountGate entitlement="cpd_developing_operational_maturity" productLabel="Final Assessment"><AssessmentRunner slug="developing-operational-maturity" /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/courses/developing-operational-maturity/assessment-feedback" element={<PublicShell><MyAccountGate entitlement="cpd_developing_operational_maturity" productLabel="Your feedback"><AssessmentFeedback slug="developing-operational-maturity" /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/courses/developing-operational-maturity/certificate" element={<PublicShell><MyAccountGate entitlement="cpd_developing_operational_maturity" productLabel="Certificate"><CertificateView slug="developing-operational-maturity" /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/courses/developing-operational-maturity/feedback" element={<PublicShell><MyAccountGate entitlement="cpd_developing_operational_maturity" productLabel="Course feedback"><CourseFeedback slug="developing-operational-maturity" /></MyAccountGate></PublicShell>} />

                    <Route path="/cpd/pet-level-3-cpd-40-hours" element={<PublicShell><PetLevel3Landing /></PublicShell>} />
                    {/* PET L3 native delivery (purchase flow still external until the owner flips the switch). */}
                    <Route path="/my-account/courses/pet-level-3-cpd-40-hours" element={<PublicShell><MyAccountGate entitlement="cpd_pet_l3" productLabel="Pre-Employment Training Level 3"><CoursePlayer slug="pet-level-3-cpd-40-hours" /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/courses/pet-level-3-cpd-40-hours/assessment" element={<PublicShell><MyAccountGate entitlement="cpd_pet_l3" productLabel="Final Assessment"><AssessmentRunner slug="pet-level-3-cpd-40-hours" /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/courses/pet-level-3-cpd-40-hours/assessment-feedback" element={<PublicShell><MyAccountGate entitlement="cpd_pet_l3" productLabel="Your feedback"><AssessmentFeedback slug="pet-level-3-cpd-40-hours" /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/courses/pet-level-3-cpd-40-hours/certificate" element={<PublicShell><MyAccountGate entitlement="cpd_pet_l3" productLabel="Certificate"><CertificateView slug="pet-level-3-cpd-40-hours" /></MyAccountGate></PublicShell>} />
                    <Route path="/my-account/courses/pet-level-3-cpd-40-hours/feedback" element={<PublicShell><MyAccountGate entitlement="cpd_pet_l3" productLabel="Course feedback"><CourseFeedback slug="pet-level-3-cpd-40-hours" /></MyAccountGate></PublicShell>} />

                    <Route path="/verify" element={<PublicShell><Verify /></PublicShell>} />
                    <Route path="/my-account/interview" element={<PublicShell><MyAccountGate entitlement="interview_simulator" productLabel="Interview Coach"><InterviewProgress /></MyAccountGate></PublicShell>} />

                    {/* Referral Partner Programme */}
                    <Route path="/referral-programme" element={<PublicShell><ReferralProgramme /></PublicShell>} />
                    <Route path="/referral-programme/terms" element={<PublicShell><ReferralTerms /></PublicShell>} />
                    <Route path="/r/:code" element={<ReferralRedirect />} />

                    {/* Hidden post-checkout URLs */}
                    <Route path="/submit-cv-x83klp9" element={<PublicShell><SubmitCV /></PublicShell>} />
                    <Route path="/submit-cv-X83kLp9" element={<PublicShell><SubmitCV /></PublicShell>} />
                    <Route path="/download-interview-x72k9pq" element={<PublicShell><DownloadInterview /></PublicShell>} />
                    <Route path="/online-assessment-sth65bsd4" element={<PublicShell><OnlineAssessmentTool /></PublicShell>} />
                    <Route path="/Full-Package68KaSWom86" element={<PublicShell><FullPackageAccess /></PublicShell>} />
                    <Route path="/Complete-Package8fFx23" element={<PublicShell><CompletePackageAccess /></PublicShell>} />

                    {/* Campaign and blog routes */}
                    {/* /armed-forces-week is redirected to /service-leavers
                        while the 2026 campaign is off-season. The page
                        template is retained (import above) so we can
                        reactivate for Armed Forces Week 2027. */}
                    <Route path="/armed-forces-week" element={<Navigate to="/service-leavers" replace />} />
                    <Route path="/armed-forces-week-2026" element={<Navigate to="/service-leavers" replace />} />
                    <Route path="/armed-forces-week-2027" element={<PublicShell><ArmedForcesWeek /></PublicShell>} />

                    <Route path="/blog/why-most-trainee-train-driver-applicants-fail" element={<PublicShell><WhyMostApplicantsFail /></PublicShell>} />
                    <Route path="/blog/what-train-driver-recruiters-are-actually-looking-for" element={<PublicShell><WhatTrainDriverRecruitersLookFor /></PublicShell>} />
                    <Route path="/blog/what-really-happens-at-a-train-driver-assessment-day" element={<PublicShell><AssessmentDayReality /></PublicShell>} />
                    <Route path="/blog/railway-experience-does-not-guarantee-success" element={<RailwayExperienceDoesNotGuaranteeSuccess />} />

                    <Route path="/blog" element={<PublicShell><Blog /></PublicShell>} />

                    {/* Legacy URL redirects */}
                    {LEGACY_REDIRECTS.map(([from, to]) => (
                      <Route
                        key={from}
                        path={from}
                        element={<Navigate to={to} replace />}
                      />
                    ))}

                    <Route path="*" element={<PublicShell><NotFound /></PublicShell>} />
                  </Routes>
                  <CheckoutGate />
                </UserAuthProvider>
                </AffiliateAuthProvider>
                </DtmAuthProvider>
              </AdminAuthProvider>
            </TrackingProvider>
            </ErrorBoundary>
          </SiteContentProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
